import { useEffect, useMemo } from 'react';
import {
  LocationFeatureResponse,
  LocationsFeaturesResponse,
} from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/location_features.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { AnalyticsCommonTypes } from '@frontend/api-analytics';
import { CustomizationFlagQueries, CustomizationFlagTypes } from '@frontend/api-customization-flags';
import { useHasAclAccess } from '@frontend/analytics-shared';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { VOICE_AIR_CUSTOMIZATION_FEATURE_ENUMS } from '@frontend/shared';
import { useCallIntelDemoFlags } from './use-call-intel-demo-flags';

// Define the possible location scopes. Use 'scoped' when want to access from GLS and 'allAccess' when want to access all locations user has access to across Org.
type LocationScope = 'scoped' | 'allAccess';

type Params = {
  locationScope?: LocationScope;
  onLoadLocations?: (locations: AnalyticsCommonTypes.StringRecord) => void;
};

type LocationFlagMaps = {
  combined: Record<string, LocationFeatureResponse | undefined>;
  callIntel: Record<string, LocationFeatureResponse | undefined>;
  voiceAi: Record<string, LocationFeatureResponse | undefined>;
};

type Response = {
  hasAccessToMultipleLocations?: boolean;
  isLoading?: boolean;
  isMultiLocation?: boolean;
  locations: AnalyticsCommonTypes.StringRecord;
  preselectedAccessibleLocations: AnalyticsCommonTypes.StringRecord;
  locationIds: string[];
  callIntelLocationIds: string[];
  voiceAiLocationIds: string[];
};

const CALL_INTELLIGENCE_FLAG = 'callintelligence';

const normalizeFlagName = (name?: string) => (name ?? '').toLowerCase().replace(/\s+/g, '');

const isActiveOrPromote = (state?: CustomizationFlagTypes.CustomizationFlagState) => {
  return (
    !!state &&
    [
      CustomizationFlagTypes.CustomizationFlagState.ACTIVE,
      CustomizationFlagTypes.CustomizationFlagState.PROMOTE,
    ].includes(state)
  );
};

const buildLocalFlagMapFromBatched = (data: LocationsFeaturesResponse, allowedIds: string[]): LocationFlagMaps => {
  const byLocation = data?.locationsFeatures ?? {};
  const allowedSet = new Set(allowedIds);
  const combinedMap: Record<string, LocationFeatureResponse | undefined> = {};
  const callIntelMap: Record<string, LocationFeatureResponse | undefined> = {};
  const voiceAiMap: Record<string, LocationFeatureResponse | undefined> = {};

  Object.entries(byLocation).forEach(([locationId, flagsData]) => {
    if (!allowedSet.has(locationId)) return;

    const features = flagsData?.features ?? [];

    const callIntelMatch = features.find((f) => normalizeFlagName(f?.name) === CALL_INTELLIGENCE_FLAG);

    // Find the first voice AI feature that is active/promote.
    // We must check each voice AI flag individually — if the first match (e.g. TRUELARK_AI_VOICE)
    // is inactive, we must continue to check the next (e.g. WEAVE_VOICE_AIR).
    const voiceAiMatch = features.find(
      (f) =>
        !!f?.featureEnum &&
        VOICE_AIR_CUSTOMIZATION_FEATURE_ENUMS.some((enumVal) => enumVal === f.featureEnum) &&
        isActiveOrPromote(f?.state)
    );

    const isCallIntelActive = isActiveOrPromote(callIntelMatch?.state);
    const isVoiceAiActive = !!voiceAiMatch;

    if (isCallIntelActive && callIntelMatch) {
      callIntelMap[locationId] = callIntelMatch;
    }
    if (isVoiceAiActive && voiceAiMatch) {
      voiceAiMap[locationId] = voiceAiMatch;
    }

    if (isCallIntelActive && callIntelMatch) {
      combinedMap[locationId] = callIntelMatch;
    } else if (isVoiceAiActive && voiceAiMatch) {
      combinedMap[locationId] = voiceAiMatch;
    }
  });

  return { combined: combinedMap, callIntel: callIntelMap, voiceAi: voiceAiMap };
};

/**
 * Filters a location flag map to include only locations with an ACTIVE or PROMOTE flag state.
 */
const filterActiveLocations = (
  flagMap: Record<string, LocationFeatureResponse | undefined>,
  getScopeName: (id: string) => string
): AnalyticsCommonTypes.StringRecord => {
  const activeEntries = Object.entries(flagMap).filter(([, value]) => {
    return isActiveOrPromote(value?.state);
  });

  return activeEntries.reduce<AnalyticsCommonTypes.StringRecord>((acc, [locationId]) => {
    acc[locationId] = getScopeName(locationId);
    return acc;
  }, {});
};

/**
 * Sorts a record of locations alphabetically by name.
 */
const sortLocations = (locations: AnalyticsCommonTypes.StringRecord): AnalyticsCommonTypes.StringRecord => {
  return Object.fromEntries(Object.entries(locations).sort(([, a], [, b]) => a.localeCompare(b)));
};

/**
 * Hook to fetch and manage call intelligence locations.
 * Determines the scope of locations, fetches customization flags, and returns the final list of locations.
 */
export const useCallIntelLocations = (options?: Params): Response => {
  const { locationScope, onLoadLocations } = options || {};
  const { isDemoAccount } = useCallIntelDemoFlags();

  const { isSingleTypeScope, getScopeName, selectedLocationIds = [] } = useAppScopeStore();

  const { getCustomizationFlagLocationMap: getScopedCustomizationFlagLocationMap } = useScopedAppFlagStore();

  const resolvedScope: LocationScope = locationScope ?? 'allAccess';

  const { locationIdsWithAclAccess } = useHasAclAccess({
    isAllAccessScopeEnabled: resolvedScope === 'allAccess' && !isDemoAccount,
    isDemoAccount,
    permissions: [Permission.CALL_INTELLIGENCE_READ],
  });

  // Determine if customization flags for all accessible locations should be fetched. For scoped access, we use the scoped flags.
  const shouldFetchAllAccessibleLocationFlags = resolvedScope === 'allAccess' && locationIdsWithAclAccess.length > 0;

  // Fetch customization flags for all accessible locations
  const { data: customizationFlagsData, isLoading: isAllAccessLoading } =
    CustomizationFlagQueries.useBatchedMultiCustomizationFlags(locationIdsWithAclAccess, {
      enabled: shouldFetchAllAccessibleLocationFlags,
    });

  // Build maps of location flags based on the resolved scope
  const locationFlagMaps = useMemo((): LocationFlagMaps => {
    if (resolvedScope === 'allAccess') {
      if (!shouldFetchAllAccessibleLocationFlags || isAllAccessLoading || !customizationFlagsData) {
        return { combined: {}, callIntel: {}, voiceAi: {} };
      }
      return buildLocalFlagMapFromBatched(customizationFlagsData, locationIdsWithAclAccess);
    }

    // For scoped access, build separate maps for Call Intelligence and Voice AIR
    const callIntelScopedMap = getScopedCustomizationFlagLocationMap(CALL_INTELLIGENCE_FLAG);

    // Get Voice AIR maps for all AI receptionist flags
    const voiceAIRScopedMaps = VOICE_AIR_CUSTOMIZATION_FEATURE_ENUMS.map((featureEnum) =>
      getScopedCustomizationFlagLocationMap(featureEnum)
    );

    const callIntelMap: Record<string, LocationFeatureResponse | undefined> = {};
    const voiceAiMap: Record<string, LocationFeatureResponse | undefined> = {};
    const combinedMap: Record<string, LocationFeatureResponse | undefined> = {};

    // Build Voice AIR map by checking all AI receptionist flags
    voiceAIRScopedMaps.forEach((voiceAiScopedMap) => {
      Object.entries(voiceAiScopedMap).forEach(([locationId, flag]) => {
        if (flag && isActiveOrPromote(flag.state) && !voiceAiMap[locationId]) {
          voiceAiMap[locationId] = flag;
          combinedMap[locationId] = flag;
        }
      });
    });

    // Build Call Intelligence map (overrides Voice AIR in combined map)
    Object.entries(callIntelScopedMap).forEach(([locationId, flag]) => {
      if (flag && isActiveOrPromote(flag.state)) {
        callIntelMap[locationId] = flag;
        combinedMap[locationId] = flag; // CI takes priority in combined map
      }
    });

    return { combined: combinedMap, callIntel: callIntelMap, voiceAi: voiceAiMap };
  }, [
    customizationFlagsData,
    getScopedCustomizationFlagLocationMap,
    isAllAccessLoading,
    resolvedScope,
    shouldFetchAllAccessibleLocationFlags,
  ]);

  // Get active call-intel locations based on the combined location flags
  const activeLocations = useMemo(
    () => filterActiveLocations(locationFlagMaps.combined, getScopeName),
    [locationFlagMaps.combined, getScopeName]
  );

  const callIntelLocationIds = useMemo(() => Object.keys(locationFlagMaps.callIntel), [locationFlagMaps.callIntel]);
  const voiceAiLocationIds = useMemo(() => Object.keys(locationFlagMaps.voiceAi), [locationFlagMaps.voiceAi]);

  const sortedLocations: AnalyticsCommonTypes.StringRecord = useMemo(() => {
    if (isDemoAccount) {
      const demoLocations = Object.fromEntries(
        locationIdsWithAclAccess.map((locationId) => [locationId, getScopeName(locationId)])
      );
      return sortLocations(demoLocations);
    }
    return sortLocations(activeLocations);
  }, [activeLocations, getScopeName, isDemoAccount]);

  const sortedLocationsIds = useMemo(() => Object.keys(sortedLocations), [sortedLocations]);

  const preselectedAccessibleLocations: AnalyticsCommonTypes.StringRecord = useMemo(() => {
    const allLocationsIds = new Set(Object.keys(sortedLocations));
    return selectedLocationIds.reduce<AnalyticsCommonTypes.StringRecord>((acc, id) => {
      if (allLocationsIds.has(id)) acc[id] = getScopeName(id);
      return acc;
    }, {});
  }, [selectedLocationIds, sortedLocations, getScopeName]);

  const hasAccessToMultipleLocations = useMemo(
    () => resolvedScope === 'allAccess' && locationIdsWithAclAccess.length > 1,
    [locationIdsWithAclAccess.length, resolvedScope]
  );

  const isMultiLocation = useMemo(
    () => (resolvedScope === 'allAccess' ? hasAccessToMultipleLocations : !isSingleTypeScope),
    [hasAccessToMultipleLocations, isSingleTypeScope, resolvedScope]
  );

  useEffect(() => {
    if (sortedLocations && onLoadLocations) {
      onLoadLocations(sortedLocations);
    }
  }, [sortedLocations]);

  return {
    hasAccessToMultipleLocations,
    isMultiLocation,
    locations: sortedLocations,
    locationIds: sortedLocationsIds,
    preselectedAccessibleLocations,
    isLoading: isAllAccessLoading,
    callIntelLocationIds,
    voiceAiLocationIds,
  };
};
